import React, { useState, useEffect, useRef, useCallback } from 'react';
import { reverseGeocode } from '../lib/valhalla';

/**
 * LocationAutocomplete — a city/address autocomplete input backed by
 * Nominatim (OpenStreetMap). Replaces the plain-text Origin/Destination
 * fields so users can disambiguate same-named cities and don't risk
 * silent typo-routing.
 *
 * On selection, calls `onSelect(location)` with:
 *   { lat, lng, name, country_code, display_name }
 *
 * The parent stores this structured object and uses it directly for
 * routing — no second geocode round-trip needed.
 *
 * Why Nominatim:
 *   - Free, no API key
 *   - Already used by the rest of the app's geocode() function
 *   - Returns addressdetails so we can show country/state for disambiguation
 *
 * Polite-use compliance:
 *   - User-Agent header identifying the app (required by Nominatim ToS)
 *   - Max ~1 req/sec via 300ms debouncing
 *   - Limit of 8 results per request
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;
const MAX_RESULTS = 8;

export default function LocationAutocomplete({
  value,           // string — the typed text (controlled)
  onChange,        // (text) => void — fires on every keystroke
  onSelect,        // (location | null) => void — fires when user picks an option
  placeholder,
  iconColor,       // 'green' (default) | 'red'
  autoFocus = false,
  showCurrentLocation = false, // when true, shows a "use my location" button
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    // Clear any pending debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Abort any in-flight request
    if (abortRef.current) abortRef.current.abort();

    const trimmed = (value || '').trim();
    if (trimmed.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const url = new URL(NOMINATIM_URL);
        url.searchParams.set('q', trimmed);
        url.searchParams.set('format', 'json');
        url.searchParams.set('limit', String(MAX_RESULTS));
        url.searchParams.set('addressdetails', '1');
        const res = await fetch(url.toString(), {
          signal: ctrl.signal,
          // Nominatim requires a custom UA; browsers won't let us set it
          // directly, but they do send Referer which Nominatim accepts.
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`Nominatim ${res.status}`);
        const data = await res.json();
        if (ctrl.signal.aborted) return;
        setSuggestions(Array.isArray(data) ? data : []);
        setActiveIndex(-1);
        setOpen(true);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Non-blocking: just no suggestions, user can still type and submit
          setSuggestions([]);
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  // Close on outside click
  useEffect(() => {
    function onClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const selectSuggestion = useCallback((s) => {
    const location = {
      lat: parseFloat(s.lat),
      lng: parseFloat(s.lon),
      name: shortName(s),
      country_code: s.address?.country_code
        ? s.address.country_code.toUpperCase()
        : null,
      display_name: s.display_name,
    };
    onChange(location.name);
    onSelect(location);
    setOpen(false);
    setSuggestions([]);
  }, [onChange, onSelect]);

  /**
   * "Use my location" — asks the browser for GPS coordinates, reverse-geocodes
   * them to a readable place + country code, and fills the field as if the
   * user had picked it from the dropdown.
   *
   * Browser geolocation requires HTTPS and an explicit user permission grant;
   * the deployed app is HTTPS so that's fine. We handle the common failure
   * modes (denied, unavailable, timeout) with a short inline message rather
   * than a blocking alert.
   */
  const useMyLocation = useCallback(() => {
    setGeoError('');
    if (!('geolocation' in navigator)) {
      setGeoError('Location not supported on this device');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const loc = await reverseGeocode(latitude, longitude);
        if (loc) {
          onChange(loc.name);
          onSelect(loc);
          setOpen(false);
          setSuggestions([]);
        } else {
          // Reverse-geocode failed but we still have coordinates — use them
          // with a coordinate label so the trip can still be planned.
          const fallback = {
            lat: latitude,
            lng: longitude,
            name: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            country_code: null,
          };
          onChange(fallback.name);
          onSelect(fallback);
        }
        setGeoLoading(false);
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError('Location permission denied');
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGeoError('Location unavailable');
        } else {
          setGeoError('Could not get your location');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  }, [onChange, onSelect]);

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      // Allow Enter to submit form when no dropdown is open
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div className="field">
        <span className={`field-icon${iconColor === 'red' ? ' red' : ''}`}>
          {iconColor === 'red' ? '◉' : '●'}
        </span>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value || ''}
          onChange={(e) => {
            onChange(e.target.value);
            // Clear any prior selection if user re-edits — they need to re-pick
            onSelect(null);
          }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
        />
        {loading && (
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 6 }}>
            …
          </span>
        )}
        {showCurrentLocation && (
          <button
            type="button"
            onClick={useMyLocation}
            disabled={geoLoading}
            title="Use my current location"
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: 'none',
              cursor: geoLoading ? 'default' : 'pointer',
              color: 'var(--accent-primary)',
              fontSize: 14,
              padding: '0 2px',
              marginLeft: 4,
            }}
          >
            {geoLoading ? '…' : '⌖'}
          </button>
        )}
      </div>

      {showCurrentLocation && geoError && (
        <p style={{ fontSize: 10, color: 'var(--accent-danger)', margin: '4px 0 0 4px' }}>
          {geoError}
        </p>
      )}

      {open && suggestions.length > 0 && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: 'var(--bg-card)',
            border: '0.5px solid var(--border-default)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            maxHeight: 320,
            overflowY: 'auto',
            zIndex: 100,
          }}
        >
          {suggestions.map((s, i) => {
            const isActive = i === activeIndex;
            return (
              <div
                key={s.place_id || i}
                role="option"
                aria-selected={isActive}
                onMouseDown={(e) => {
                  // mousedown (not click) so it fires before the input's blur
                  e.preventDefault();
                  selectSuggestion(s);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                style={{
                  padding: '10px 14px',
                  cursor: 'pointer',
                  borderBottom: i < suggestions.length - 1
                    ? '0.5px solid var(--border-subtle)'
                    : 'none',
                  background: isActive ? 'var(--bg-card-hover)' : 'transparent',
                  fontSize: 13,
                  color: 'var(--text-primary)',
                }}
              >
                <div style={{ fontWeight: 500 }}>{shortName(s)}</div>
                <div style={{
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  marginTop: 2,
                }}>
                  {contextLine(s)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && !loading && suggestions.length === 0 && (value || '').trim().length >= MIN_QUERY_LEN && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: 'var(--bg-card)',
          border: '0.5px solid var(--border-default)',
          borderRadius: 10,
          padding: '10px 14px',
          fontSize: 12,
          color: 'var(--text-tertiary)',
          zIndex: 100,
        }}>
          No matches. Check your spelling or try a nearby larger city.
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

/**
 * The first chunk of `display_name` — typically the city/place itself.
 * E.g. "Mumbai, Maharashtra, India" → "Mumbai"
 */
function shortName(s) {
  if (!s.display_name) return '';
  return s.display_name.split(',')[0].trim();
}

/**
 * The disambiguating context: state + country, skipping the first chunk.
 * E.g. "Mumbai, Maharashtra, India" → "Maharashtra, India"
 * For "Springfield, Illinois, United States" → "Illinois, United States"
 *
 * This is the line that lets users tell the difference between two cities
 * with the same name. It's the whole point of this component.
 */
function contextLine(s) {
  if (!s.display_name) return '';
  const parts = s.display_name.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length <= 1) return '';
  // Drop the first part (already shown as primary name)
  // Show a few middle parts for context, with country always at the end
  const rest = parts.slice(1);
  // Trim if too long — keep first 2 and last 1 (usually state + country)
  if (rest.length > 3) {
    return `${rest.slice(0, 2).join(', ')} … ${rest[rest.length - 1]}`;
  }
  return rest.join(', ');
}
